import Vue from 'vue';
import translations from './translations';
import { LOCALES, DEFAULT_LOCALE, localePath } from './locales';

export const SUPPORTED_LOCALES = LOCALES;
export { DEFAULT_LOCALE };

const STORAGE_KEY = 'emdr-lang';

const state = Vue.observable({ locale: DEFAULT_LOCALE });

export function getLocale() {
  return state.locale;
}

export function setLocale(locale) {
  const resolved = SUPPORTED_LOCALES.indexOf(locale) !== -1 ? locale : DEFAULT_LOCALE;
  state.locale = resolved;

  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = resolved;
  }

  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.setItem(STORAGE_KEY, resolved);
    } catch (e) {
      // localStorage unavailable (private mode, etc.) - ignore
    }
  }

  return resolved;
}

// Only returns an explicit, previously-saved user preference.
// Never guesses - used to avoid overriding a deliberate language choice.
export function getStoredLocale() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LOCALES.indexOf(stored) !== -1 ? stored : null;
  } catch (e) {
    return null;
  }
}

// Browser-language based detection, used only as a fallback for a first-time
// visitor landing on a locale-less URL (e.g. "/"). Never used to decide what
// gets rendered for a URL that already carries an explicit locale (e.g. "/pl"),
// so crawlers always get stable, deterministic content per URL.
// The first of the site's languages in the browser's list wins, in the
// visitor's own order of preference: ['nl-NL', 'en'] gets Dutch, but an
// expat with ['en-US', 'nl-NL'] (English browser in the Netherlands) gets
// English. Languages the site doesn't have are skipped.
export function detectBrowserLocale() {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  const candidates = (navigator.languages && navigator.languages.length)
    ? navigator.languages
    : [navigator.language];
  for (const lang of candidates) {
    const code = typeof lang === 'string' ? lang.toLowerCase().slice(0, 2) : '';
    if (SUPPORTED_LOCALES.indexOf(code) !== -1) return code;
  }
  return DEFAULT_LOCALE;
}

export function t(key) {
  const dict = translations[state.locale] || translations[DEFAULT_LOCALE];
  const value = key.split('.').reduce((obj, k) => (obj && obj[k] !== undefined ? obj[k] : undefined), dict);
  if (value !== undefined) return value;

  const fallbackDict = translations[DEFAULT_LOCALE];
  const fallbackValue = key.split('.').reduce((obj, k) => (obj && obj[k] !== undefined ? obj[k] : undefined), fallbackDict);
  return fallbackValue !== undefined ? fallbackValue : key;
}

// Polish has three plural forms: 1 notatka (one), 2-4 notatki (few, except
// 12-14), 5+ / 0 notatek (many). English only distinguishes one/many.
function pluralForm(locale, n) {
  if (locale === 'pl') {
    if (n === 1) return 'one';
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return 'few';
    return 'many';
  }
  return n === 1 ? 'one' : 'many';
}

// Count-aware translation: the key must resolve to an object with
// { one, few, many } forms (few optional for locales that don't need it).
export function tc(key, count) {
  const forms = t(key);
  if (typeof forms !== 'object' || forms === null) return String(forms);
  const form = pluralForm(state.locale, count);
  return forms[form] || forms.many || forms.one || key;
}

export const i18nPlugin = {
  install(VueCtor) {
    VueCtor.mixin({
      computed: {
        $locale() {
          return state.locale;
        },
      },
      methods: {
        $t(key) {
          return t(key);
        },
        $tc(key, count) {
          return tc(key, count);
        },
        // '/login' -> '/pl/login' on the Polish site
        $localePath(path) {
          return localePath(path, state.locale);
        },
      },
    });
  },
};
